/**
 * EdgeDocs Type Definitions
 *
 * Core type system for EdgeDocs editor with system font support (Phase 1A)
 */

import type { FormattingSpan } from '../../core/types/FormattingSpan';

// ============================================================================
// SYSTEM FONT CONSTANTS
// ============================================================================

export const SYSTEM_FONT_STACK = {
  // Serif (legal document standard)
  // NOTE: EdgeDocs Roman is loaded via WebFontLoader (NimbusRoman-Regular.otf)
  // for court compliance (edgelexlaw-realm). It provides accurate metrics matching WASM.
  // The unique name prevents browser from using system fonts instead.
  serif: `'EdgeDocs Roman', 'Times New Roman', 'Times', 'Liberation Serif', 'DejaVu Serif', serif`,

  // Sans-serif (UI elements, modern docs)
  sans: `'Segoe UI', 'Roboto', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif`,

  // Monospace (code, technical docs)
  mono: `'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace`
} as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Available fonts (matches backend /assets/fonts/)
export type DocumentFontType = 
  | 'Times New Roman'
  | 'EdgeDocs Roman'  // ✅ Court-compliant NimbusRoman-Regular.otf
  | 'Inter'
  | 'Roboto'
  | 'DejaVu Sans'
  | 'DejaVu Serif'
  | 'DejaVu Sans Mono'
  | 'Liberation Serif'
  | 'Liberation Sans'
  | 'Nimbus Roman'
  | 'TeX Gyre Termes'
  | 'Source Serif 4'
  | 'Noto Sans'
  | 'JetBrains Mono';

export const DEFAULT_DOCUMENT_FONT: DocumentFontType = 'Times New Roman'; // Legal standard
export const DEFAULT_UI_FONT: DocumentFontType = 'Inter';                 // Modern UI

/**
 * Map specific font names to font categories (for legacy SystemFontRenderer)
 * WASM uses specific font names, but canvas fallback needs font stacks
 */
export function getFontCategory(fontName: DocumentFontType): keyof typeof SYSTEM_FONT_STACK {
  // Serif fonts
  if (fontName === 'Times New Roman' || 
      fontName === 'Liberation Serif' || 
      fontName === 'DejaVu Serif' ||
      fontName === 'Nimbus Roman' ||
      fontName === 'TeX Gyre Termes' ||
      fontName === 'Source Serif 4') {
    return 'serif';
  }
  
  // Monospace fonts
  if (fontName === 'JetBrains Mono' || 
      fontName === 'DejaVu Sans Mono') {
    return 'mono';
  }
  
  // Sans-serif fonts (default)
  return 'sans';
}

// ============================================================================
// FONT METRICS & CONFIGURATION
// ============================================================================

export interface SystemFontMetrics {
  family: string;
  size: number; // px
  lineHeight: number; // px
  weight: number;
  style: 'normal' | 'italic';
}

export interface SystemFontConfig {
  documentFont: DocumentFontType;
  uiFont: DocumentFontType;
  fontSizePx: number; // px at 96dpi (world units)
  fontWeight?: number; // ✅ NEW: Font weight (400 = Regular, 700 = Bold)
  fontStyle?: 'normal' | 'italic' | 'oblique'; // ✅ NEW: Font style
  lineHeight: number; // multiplier
}

// ============================================================================
// SELECTION & EDITING
// ============================================================================

// === Selection primitives ===
export type CharIndex = number;

export interface SelectionRange {
  anchor: CharIndex;  // where selection started
  focus: CharIndex;   // current caret; equals anchor when collapsed
}

// === Edit ops for history ===
export type EditOpType = 'insert' | 'delete' | 'replace' | 'paste' | 'cut';

export interface EditOperation {
  type: EditOpType;
  at: CharIndex;            // insertion point or start of range
  deletedText?: string;     // present for delete/replace/cut
  insertedText?: string;    // present for insert/replace/paste
  before: SelectionRange;   // selection before op
  after: SelectionRange;    // selection after op
  ts: number;               // timestamp for coalescing
  group?: string;           // optional typing group id for coalescing
}

// === Undo/redo stacks ===
export interface HistoryState {
  undo: EditOperation[];
  redo: EditOperation[];
  maxDepth: number; // e.g. 200
}

// === Clipboard payload (plain text for v1; extend to HTML later) ===
export interface ClipboardPayload {
  text: string;
}

// ============================================================================
// TEXT RENDERING
// ============================================================================

export interface TextRenderConfig {
  text: string;
  font: SystemFontMetrics;
  color: string;
  position: { x: number; y: number };
  maxWidth?: number;
  baseline?: 'top' | 'middle' | 'bottom' | 'alphabetic';
}

// ============================================================================
// DOCUMENT STATE
// ============================================================================

/**
 * Legal document paragraph types (Phase 2B)
 * Covers common structures in legal drafting for lawyers
 */
export type LegalParagraphType = 
  // General/Markdown
  | 'text'
  | 'heading'
  | 'list'
  | 'quote'
  | 'code'
  // Legal-specific (Phase 2B)
  | 'numbered_clause'      // 1., 1.1., 2.3.4.
  | 'lettered_clause'      // (a), (b), (i), (ii)
  | 'roman_clause'         // (i), (ii), (iii), (iv)
  | 'section_heading'      // SECTION 1. DEFINITIONS
  | 'recital'              // WHEREAS..., RECITAL 1.
  | 'definition'           // "Term" means "..."
  | 'parties_block'        // THIS AGREEMENT...
  | 'signature_block'      // Signed by: ___________
  | 'schedule'             // Schedule A, Exhibit 1
  | 'indent_block';        // Significant indentation

/**
 * Paragraph metadata for reactive layout (Phase 2)
 * Tracks paragraph boundaries and identity for incremental processing
 *
 * Phase 2B: Enhanced with legal-specific metadata
 */
export interface Paragraph {
  /** Stable UUID from database AST (e.g., "para-123e4567-e89b-12d3") */
  id: string;

  /** Paragraph type (Phase 2B: now supports legal types) */
  type: LegalParagraphType;

  /** Monotonic version counter (incremented on edit) */
  version: number;

  /** Start index in document.text (grapheme index, inclusive) */
  textStart: number;

  /** End index in document.text (grapheme index, exclusive) */
  textEnd: number;

  /** Optional style ID for semantic formatting */
  styleId?: string;

  // ✅ Per-Paragraph Formatting (2026-01-19)

  /**
   * Line spacing multiplier for this paragraph (1.0 = single, 1.5 = 1.5x, 2.0 = double)
   * If undefined, uses document-level default from fontSettings.lineHeightMul
   */
  lineSpacing?: number;

  /**
   * Font size in pixels for this paragraph (overrides document default)
   * If undefined, uses document-level default from fontSettings.fontSizePx
   */
  fontSizePx?: number;

  /**
   * Font family for this paragraph (overrides document default)
   * If undefined, uses document-level default from fontSettings.fontFamily
   */
  fontFamily?: string;

  /**
   * Font weight for this paragraph (400 = normal, 700 = bold)
   * If undefined, uses document-level default from fontSettings.fontWeight
   */
  fontWeight?: number;

  /**
   * Font style for this paragraph ('normal', 'italic', 'oblique')
   * If undefined, uses document-level default from fontSettings.fontStyle
   */
  fontStyle?: string;

  // ✅ Phase 2B: Legal-specific metadata

  /** Nesting level for hierarchical clauses (1.1 = level 2, 1.1.1 = level 3) */
  level?: number;

  /** Extracted numbering prefix: "1.", "(a)", "(iii)", "1.1." */
  numbering?: string;

  /** Indentation level (grapheme count of leading whitespace) */
  indentationLevel?: number;

  /** Content hash of trimmed text (for change detection) */
  contentHash?: string;

  /** Semantic tags: ['contains_definition', 'is_proviso', etc.] */
  tags?: string[];

  // ✅ Phase 3: Character-Level Formatting (2026-01-21)

  /**
   * Character-level formatting spans (MS Word "text runs")
   * Each span overrides paragraph/document defaults for a specific grapheme range.
   * Characters not covered by any span inherit paragraph/document defaults.
   * If undefined or empty, entire paragraph uses paragraph-level formatting.
   */
  formattingSpans?: FormattingSpan[];
}

export interface DocumentState {
  text: string;
  selection: SelectionRange;     // required; single source of truth
  cursorPosition?: number;       // optional; if present, must equal selection.focus

  /** Page setup configuration (database format, processed by PageSetupConverter) */
  pageSetup?: any;

  /** Font settings for document rendering */
  fontSettings?: any;

  /** Phase 2: Paragraph boundaries and metadata for reactive layout (optional during migration) */
  paragraphs?: Paragraph[];
}

// ============================================================================
// EDITOR CONFIGURATION
// ============================================================================

export interface EdgeDocsConfig {
  fonts: SystemFontConfig;
  // Page configuration removed - now handled by PageSetupConverter
  performance: {
    maxRenderTime: number; // ms
    maxMemoryUsage: number; // MB
  };
  history: {
    maxDepth: number;
  };
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

// NOTE: Page configuration has been moved to PageSetupConverter
// Use PageSetupConverter.getDefault() and PageSetupConverter.toWasmFormat()
// for page setup handling instead of hardcoded constants

export const DEFAULT_FONT_CONFIG: SystemFontConfig = {
  documentFont: DEFAULT_DOCUMENT_FONT,
  uiFont: DEFAULT_UI_FONT,
  fontSizePx: 12, // px at 96dpi
  lineHeight: 1.2  // Revert to original - the issue is elsewhere
};

export const DEFAULT_EDGEDOCS_CONFIG: EdgeDocsConfig = {
  fonts: DEFAULT_FONT_CONFIG,
  // Page configuration removed - use PageSetupConverter.getDefault() instead
  performance: {
    maxRenderTime: 16, // ms
    maxMemoryUsage: 50 // MB
  },
  history: {
    maxDepth: 200
  }
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

export interface Point {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TextMeasurement {
  width: number;
  height: number;
  ascent: number;
  descent: number;
}

// ============================================================================
// EVENT TYPES
// ============================================================================

export interface EditorEvent {
  type:
    | 'text-changed'
    | 'cursor-moved'
    | 'selection-changed'
    | 'render-complete'
    | 'undo'
    | 'redo'
    | 'copy'
    | 'cut'
    | 'paste'
    | 'history-changed';
  timestamp: number;
  data?: any;
}

// ============================================================================
// ERROR TYPES
// ============================================================================

export interface EdgeDocsError {
  code: string;
  message: string;
  details?: any;
  recoverable: boolean;
}

// ============================================================================
// INPUT COMMAND SYSTEM
// ============================================================================

// Simple command API (keyboard/mouse handlers will create these)
export type InputCommand =
  | { kind: 'set-selection'; selection: SelectionRange }
  | { kind: 'insert-text'; text: string }             // replaces current selection if non-collapsed
  | { kind: 'delete-backward'; unit: 'char' | 'word' | 'line' }
  | { kind: 'delete-forward';  unit: 'char' | 'word' | 'line' }
  | { kind: 'copy' }
  | { kind: 'cut' }
  | { kind: 'paste'; payload: ClipboardPayload }
  | { kind: 'undo' }
  | { kind: 'redo' };

// Pure text engine helpers
export interface ApplyResult {
  state: DocumentState;   // new text + selection
  op?: EditOperation;     // optional history record
}
export type ApplyEdit = (state: DocumentState, cmd: InputCommand) => ApplyResult;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Selection utilities
export const normalizeSelection = (sel: SelectionRange) => {
  const start = Math.min(sel.anchor, sel.focus);
  const end   = Math.max(sel.anchor, sel.focus);
  const isCollapsed = start === end;
  return { ...sel, start, end, isCollapsed };
};

// Text editing algorithms
export const applyInsert = (
  state: DocumentState,
  text: string,
  ts = performance.now(),
  group?: string
): ApplyResult => {
  const { start, end } = normalizeSelection(state.selection);
  const beforeText = state.text.slice(0, start);
  const afterText  = state.text.slice(end);
  const inserted   = text;
  const nextText   = beforeText + inserted + afterText;
  const caretAt    = start + inserted.length;

  const op: EditOperation = {
    type: (start !== end) ? 'replace' : 'insert',
    at: start,
    deletedText: (start !== end) ? state.text.slice(start, end) : undefined,
    insertedText: inserted,
    before: state.selection,
    after: { anchor: caretAt, focus: caretAt },
    ts, group
  };
  return { state: { text: nextText, selection: op.after }, op };
};

export const applyDeleteRange = (
  state: DocumentState,
  delStart: CharIndex,
  delEnd: CharIndex,
  ts = performance.now()
): ApplyResult => {
  const start = Math.max(0, Math.min(delStart, delEnd));
  const end   = Math.max(start, Math.max(delStart, delEnd));
  if (start === end) return { state }; // nothing

  const beforeText = state.text.slice(0, start);
  const afterText  = state.text.slice(end);
  const deleted    = state.text.slice(start, end);
  const nextText   = beforeText + afterText;

  const caretAt = start;
  const op: EditOperation = {
    type: 'delete',
    at: start,
    deletedText: deleted,
    before: state.selection,
    after: { anchor: caretAt, focus: caretAt },
    ts
  };
  return { state: { text: nextText, selection: op.after }, op };
};
